<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Column from 'primevue/column'
import { ClipboardPlus } from 'lucide-vue-next'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import DataTable from '@lib/components/shared/DataTable.vue'
import EmptyState from '@lib/components/shared/EmptyState.vue'
import SkeletonRows from '@lib/components/shared/SkeletonRows.vue'
import ExpandRowPanel from '@lib/components/shared/ExpandRowPanel.vue'
import BulkActionBar from '@lib/components/shared/BulkActionBar.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import { useFilters, type FilterParser } from '@lib/composables/useFilters'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { sdk } from '@/graphql/client'
import { useDevicesData, type Device, type DeviceFilters } from '@/composables/useDevicesData'
import DevicesFilterBar from '@/components/domain/devices/DevicesFilterBar.vue'
import DeviceEditPanel from '@/components/domain/devices/DeviceEditPanel.vue'
import DeviceMobileCard from '@/components/domain/devices/DeviceMobileCard.vue'
import { useClipboardStore } from '@/stores/clipboard'
import type { LocationQueryValue } from 'vue-router'

const { t } = useI18n()
const ui = useUiStore()
const auth = useAuthStore()
const toast = useToast()
const { confirm } = useConfirm()
const clipboard = useClipboardStore()

// Track ids currently being fetched so we can disable the button + avoid races.
const addingToClipboard = ref<Set<number>>(new Set())

async function addToClipboard(row: Device, ev?: Event) {
    ev?.stopPropagation()
    if (addingToClipboard.value.has(row.id)) return
    addingToClipboard.value = new Set([...addingToClipboard.value, row.id])
    try {
        const r = await sdk.AdminDeviceCredentials({ id: row.id })
        const pwd = r.admin?.device?.detail?.password ?? ''
        clipboard.add({
            kind: 'login',
            login: { username: row.username, password: pwd },
            item: { id: row.id, name: row.name || row.username }
        })
        toast.success(t('devices.qr.added'))
    } catch {
        toast.error(t('common.errors.generic'))
    } finally {
        const next = new Set(addingToClipboard.value)
        next.delete(row.id)
        addingToClipboard.value = next
    }
}

const initialFilters: DeviceFilters = {
    search: ''
}

function asString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string {
    if (Array.isArray(raw)) return raw[0] ?? ''
    return raw ?? ''
}

const parsers: { [K in keyof DeviceFilters]?: FilterParser<DeviceFilters[K]> } = {
    search: asString
}

const { filters } = useFilters<DeviceFilters>(initialFilters, { parsers })

const data = useDevicesData()

watch(
    filters,
    v => {
        data.filters.value = { ...v }
    },
    { immediate: true, deep: true }
)

const expandedId = ref<number | null>(null)
const creating = ref(false)
const selected = ref<Device[]>([])
// Both desktop (md+) and mobile (< md) branches render in the DOM (only one
// is visible per Tailwind hidden classes). Each branch has its own template
// ref to avoid v-for collisions; activeEditRef picks whichever is currently
// mounted with a panel inside. See EventsView.vue (commit 3a40bae).
type EditPanelHandle = { isDirty: () => boolean }
const editPanelRefDesktop = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
const editPanelRefMobile = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
function unwrap(r: EditPanelHandle | EditPanelHandle[] | null): EditPanelHandle | null {
    if (!r) return null
    if (Array.isArray(r)) return r.find(x => x != null) ?? null
    return r
}
function activeEditRef(): EditPanelHandle | null {
    return unwrap(editPanelRefMobile.value) ?? unwrap(editPanelRefDesktop.value)
}
function safeIsDirty(): boolean {
    try {
        return activeEditRef()?.isDirty() ?? false
    } catch {
        return false
    }
}

const NEW_ROW_ID = -1

const expandedRows = computed<Record<string, boolean>>(() => {
    if (creating.value) return { [String(NEW_ROW_ID)]: true }
    if (expandedId.value === null) return {}
    return { [String(expandedId.value)]: true }
})

const rows = computed<Device[]>(() => {
    if (creating.value) {
        const newRow: Device = { id: NEW_ROW_ID, name: '', username: '' }
        return [newRow, ...data.filteredDevices.value]
    }
    return data.filteredDevices.value
})

const isEmpty = computed(() => !data.loading.value && rows.value.length === 0)

onMounted(async () => {
    ui.setBreadcrumbs([{ label: t('devices.list.title') }])
    await data.refetch()
})

async function toggleExpand(id: number) {
    if (expandedId.value === id) {
        if (safeIsDirty()) {
            const ok = await confirm({
                title: t('devices.confirm.unsavedChanges'),
                severity: 'danger'
            })
            if (!ok) return
        }
        expandedId.value = null
    } else {
        expandedId.value = id
    }
}

function onRowClick(row: Device) {
    if (row.id === NEW_ROW_ID) return
    toggleExpand(row.id)
}

function clearFilters() {
    filters.value = { ...initialFilters }
}

function startCreate() {
    if (creating.value) return
    expandedId.value = null
    creating.value = true
}

async function onSaved() {
    creating.value = false
    expandedId.value = null
    await data.refetch()
}

async function onDeleted() {
    expandedId.value = null
    await data.refetch()
}

async function onCancel() {
    if (creating.value && safeIsDirty()) {
        const ok = await confirm({
            title: t('devices.confirm.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    creating.value = false
    expandedId.value = null
}

async function onBulkDelete() {
    if (selected.value.length === 0) return
    const ok = await confirm({
        title: t('devices.confirm.bulkDeleteTitle'),
        message: t('devices.confirm.bulkDeleteBody', { n: selected.value.length }),
        severity: 'danger'
    })
    if (!ok) return
    const results = await Promise.allSettled(
        selected.value.map(d => sdk.AdminDeleteDevice({ id: d.id }))
    )
    const okCount = results.filter(
        r => r.status === 'fulfilled' && r.value.admin?.device?.delete?.ok
    ).length
    const failCount = results.length - okCount
    toast.success(t('devices.bulk.deleteResult', { ok: okCount, failed: failCount }))
    selected.value = []
    await data.refetch()
}

watch(
    () => auth.invalidationKey,
    () => {
        expandedId.value = null
        creating.value = false
        selected.value = []
        data.refetch()
    }
)
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('devices.list.title')">
            <template #actions>
                <BBtn :disabled="creating" @click="startCreate">{{ t('devices.actions.newDevice') }}</BBtn>
            </template>
        </PageHeader>

        <DevicesFilterBar v-model="filters" @clear="clearFilters" />

        <BulkActionBar :selected-count="selected.length" @clear="selected = []">
            <BBtn
                severity="danger"
                size="small"
                type="button"
                :label="t('devices.bulk.deleteSelected')"
                @click="onBulkDelete"
            />
        </BulkActionBar>

        <div class="bg-white border border-gray-200 rounded-12 overflow-hidden">
            <SkeletonRows v-if="data.loading.value && rows.length === 0" :rows="8" :columns="3" />
            <EmptyState v-else-if="isEmpty" :title="t('devices.list.empty')" />
            <template v-else>
                <!--
                    Both branches render in the DOM (visibility gated by
                    hidden/md:block/md:hidden). Each owns its own
                    DeviceEditPanel ref to avoid template-ref collisions
                    across two trees.
                -->
                <!-- DESKTOP (md+): table -->
                <div class="hidden md:block">
                    <DataTable
                        :value="rows"
                        row-key="id"
                        :selection="selected"
                        :expanded-rows="expandedRows"
                        @update:selection="v => (selected = v)"
                        @row-click="onRowClick"
                    >
                        <Column selection-mode="multiple" :style="{ width: '38px' }" />
                        <Column :header="t('devices.list.cols.name')">
                            <template #body="{ data: row }">
                                <template v-if="row.id === NEW_ROW_ID">
                                    <em class="text-teal">{{ t('devices.actions.newDevice') }}</em>
                                </template>
                                <template v-else>
                                    <span class="font-medium text-ink">{{ row.name }}</span>
                                </template>
                            </template>
                        </Column>
                        <Column :header="t('devices.list.cols.username')">
                            <template #body="{ data: row }">
                                <span v-if="row.id !== NEW_ROW_ID" class="text-sm font-mono">{{ row.username }}</span>
                            </template>
                        </Column>
                        <Column :style="{ width: '50px' }">
                            <template #body="{ data: row }">
                                <button
                                    v-if="row.id !== NEW_ROW_ID"
                                    type="button"
                                    class="text-gray-500 hover:text-teal transition-colors p-1 disabled:opacity-40 disabled:cursor-wait"
                                    :title="t('devices.qr.addToClipboard')"
                                    :disabled="addingToClipboard.has(row.id)"
                                    @click="(ev: Event) => addToClipboard(row, ev)"
                                >
                                    <ClipboardPlus class="w-4 h-4" />
                                </button>
                            </template>
                        </Column>
                        <template #expansion="{ data: row }">
                            <ExpandRowPanel
                                :title="
                                    row.id === NEW_ROW_ID
                                        ? t('devices.actions.newDevice')
                                        : t('devices.list.cols.name') + ': ' + row.name
                                "
                                @close="row.id === NEW_ROW_ID ? onCancel() : toggleExpand(row.id)"
                            >
                                <DeviceEditPanel
                                    ref="editPanelRefDesktop"
                                    :device="row.id === NEW_ROW_ID ? null : row"
                                    @saved="onSaved"
                                    @deleted="onDeleted"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                    </DataTable>
                </div>
                <!-- MOBILE (< md): vertical card list -->
                <ul class="md:hidden divide-y divide-gray-100">
                    <li v-for="row in rows" :key="row.id" class="p-2">
                        <template v-if="row.id === NEW_ROW_ID">
                            <!-- New row: show the edit panel as a standalone card. -->
                            <ExpandRowPanel
                                :title="t('devices.actions.newDevice')"
                                @close="onCancel"
                            >
                                <DeviceEditPanel
                                    ref="editPanelRefMobile"
                                    :device="null"
                                    @saved="onSaved"
                                    @deleted="onDeleted"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                        <template v-else>
                            <DeviceMobileCard
                                :device="row"
                                :selected="selected.some(s => s.id === row.id)"
                                :expanded="expandedId === row.id"
                                :clipboard-busy="addingToClipboard.has(row.id)"
                                @toggle-select="
                                    selected = selected.some(s => s.id === row.id)
                                        ? selected.filter(s => s.id !== row.id)
                                        : [...selected, row]
                                "
                                @toggle-expand="toggleExpand(row.id)"
                                @add-to-clipboard="addToClipboard(row)"
                            />
                            <ExpandRowPanel
                                v-if="expandedId === row.id"
                                :title="t('devices.list.cols.name') + ': ' + row.name"
                                class="mt-2"
                                @close="toggleExpand(row.id)"
                            >
                                <DeviceEditPanel
                                    ref="editPanelRefMobile"
                                    :device="row"
                                    @saved="onSaved"
                                    @deleted="onDeleted"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                    </li>
                </ul>
            </template>
        </div>
    </DefaultLayout>
</template>
